import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, Info, X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
  id: string
  message: string
  type: ToastType
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: React.ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = `${Date.now()}-${Math.random()}`
    const toast: ToastItem = { id, message, type }

    setToasts((prev) => [...prev, toast])

    const timer = setTimeout(() => {
      removeToast(id)
    }, 3000)

    timersRef.current.set(id, timer)
  }, [removeToast])

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer))
      timersRef.current.clear()
    }
  }, [])

  const iconMap = {
    success: <CheckCircle size={18} className="text-[#5ec27a]" />,
    error: <XCircle size={18} className="text-[#e0625f]" />,
    info: <Info size={18} className="text-accent" />,
  }

  const borderColorMap = {
    success: 'border-[#5ec27a]/40',
    error: 'border-[#e0625f]/40',
    info: 'border-accent/40',
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className={`
                flex items-center gap-3 min-w-[280px] max-w-[400px]
                rounded-md border bg-surface
                px-4 py-3 shadow-lg shadow-black/40
                ${borderColorMap[toast.type]}
              `}
              role="alert"
            >
              {iconMap[toast.type]}
              <p className="flex-1 text-sm text-fg">{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-faint hover:text-fg transition-colors"
                aria-label="Dismiss toast"
              >
                <X size={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}
